import { Delete } from "lucide-react";

/**
 * On-screen numeric keypad. Renders 0–9 + backspace + enter as <button>s.
 * The PIN is held entirely by the parent component, no <input> exists in the
 * DOM, so Safari/Keychain/iOS autofill physically cannot interfere.
 *
 * Props:
 *   value      string  current PIN value (digits only)
 *   onChange   fn      called with the next value when a digit/backspace is pressed
 *   onEnter    fn?     optional: called when the "Enter" button is pressed
 *   maxLength  number  defaults to 6
 *   disabled   bool    disables every button
 *   testid     string  base data-testid prefix
 */
export const PinKeypad = ({
  value,
  onChange,
  onEnter,
  maxLength = 6,
  disabled = false,
  testid = "pin-keypad",
}) => {
  const press = (digit) => {
    if (disabled) return;
    if (value.length >= maxLength) return;
    onChange((value + digit).slice(0, maxLength));
  };

  const back = () => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  };

  const enter = () => {
    if (disabled) return;
    if (onEnter) onEnter();
  };

  const Btn = ({ children, onClick, label, danger = false, accent = false, dataTestid }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-testid={dataTestid}
      className={[
        "h-16 sm:h-[72px] font-serif text-2xl text-white",
        "border border-white/10 bg-white/[0.02]",
        "transition-all duration-200 active:scale-95",
        "disabled:opacity-30",
        danger
          ? "hover:bg-red-500/10 hover:border-red-400/40 hover:text-red-300"
          : accent
          ? "hover:bg-[#C5A059]/20 hover:border-[#C5A059]/60 hover:text-[#C5A059]"
          : "hover:bg-[#C5A059]/10 hover:border-[#C5A059]/40",
        "touch-manipulation select-none",
      ].join(" ")}
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      {children}
    </button>
  );

  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid={testid}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
        <Btn key={n} onClick={() => press(String(n))} label={`Digit ${n}`} dataTestid={`${testid}-digit-${n}`}>
          {n}
        </Btn>
      ))}
      <Btn onClick={back} label="Backspace" danger dataTestid={`${testid}-backspace`}>
        <Delete size={20} className="mx-auto" />
      </Btn>
      <Btn onClick={() => press("0")} label="Digit 0" dataTestid={`${testid}-digit-0`}>
        0
      </Btn>
      {onEnter ? (
        <Btn onClick={enter} label="Enter" accent dataTestid={`${testid}-enter`}>
          <span className="font-sans text-[11px] tracking-[0.2em] uppercase">Enter</span>
        </Btn>
      ) : (
        <div />
      )}
    </div>
  );
};

/**
 * Six dot indicator for the current PIN length.
 */
export const PinDots = ({ value, maxLength = 6, testid = "pin-dots" }) => (
  <div className="flex items-center justify-center gap-3" data-testid={testid}>
    {Array.from({ length: maxLength }).map((_, i) => {
      const filled = i < value.length;
      return (
        <div
          key={i}
          data-testid={`${testid}-${i}-${filled ? "on" : "off"}`}
          className={[
            "w-3 h-3 rounded-full transition-all duration-200",
            filled ? "bg-[#C5A059] shadow-[0_0_12px_rgba(197,160,89,0.6)]" : "border border-white/20",
          ].join(" ")}
        />
      );
    })}
  </div>
);
